/**
 * Person-Screener-only scalars surfaced as configurable indicators (so every
 * Person Scanner column is findable in the + Indicator picker, not just the
 * + Criterion builder): the open/close rule + its S/R level, the floor-pivot
 * status, and the composite Tech Rank. All read the same baked `jp_*` fields the
 * Person Screener shows, so they resolve instant (panelKey = Daily; W/M via
 * TF_PRECOMPUTED). Math mirrors mpp/person.ts `tfFields`.
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DWM, TF_PARAM } from "./timeframe";
import { resampleOHLC, htIndexPerDay, type HtFreq } from "./weekly";
import { personsPivots, type PivotInterval } from "./personsPivots";
import type { OHLCV, IndicatorParams, SharedIndicatorDef } from "../types";

const PERSON = familyGroup("ocrule"); // → "Pivot Studies"
// zero = "Neutral" so a doji OC Rule reads "Neutral" (Pivot Status is never 0).
const BULL_BEAR = { pos: "Bull", neg: "Bear", zero: "Neutral" } as const;

const tfFreq = (tf: number | undefined): HtFreq | "day" =>
  tf === 2 ? "month" : tf === 1 ? "week" : "day";
const tfInterval = (tf: number | undefined): PivotInterval =>
  tf === 2 ? "M" : tf === 1 ? "W" : "D";

/** Project a per-bucket numeric series onto daily bars by bucket index. */
function project(
  times: ReadonlyArray<number>,
  htTimes: ReadonlyArray<number>,
  vals: ReadonlyArray<number>,
): (number | null)[] {
  const idx = htIndexPerDay(times, htTimes);
  return times.map((_, i) => {
    const k = idx[i]!;
    return k >= 0 && k < vals.length ? vals[k]! : null;
  });
}

/** OC rule per bucket: close vs open → +1 Bull / -1 Bear / 0 Neutral (doji). */
function ocRuleSeries(d: OHLCV, freq: HtFreq | "day"): (number | null)[] {
  const { opens, highs, lows, closes, times } = d;
  if (!opens) return new Array(closes.length).fill(null);
  const rule = (o: number, c: number) => (c > o ? 1 : c < o ? -1 : 0);
  if (freq === "day") return closes.map((c, i) => rule(opens[i]!, c));
  if (!times || !highs || !lows) return new Array(closes.length).fill(null);
  const h = resampleOHLC(times, opens, highs, lows, closes, freq);
  return project(times, h.t, h.c.map((c, i) => rule(h.o[i]!, c)));
}

/** OC support/resistance: the swing the active side defends — Bull → bucket low
 *  (support); Bear → bucket high (resistance); Neutral → 0. */
function ocSrSeries(d: OHLCV, freq: HtFreq | "day"): (number | null)[] {
  const { opens, highs, lows, closes, times } = d;
  if (!opens || !highs || !lows) return new Array(closes.length).fill(null);
  const lvl = (o: number, c: number, hi: number, lo: number) =>
    c > o ? lo : c < o ? hi : 0;
  if (freq === "day") return closes.map((c, i) => lvl(opens[i]!, c, highs[i]!, lows[i]!));
  if (!times) return new Array(closes.length).fill(null);
  const h = resampleOHLC(times, opens, highs, lows, closes, freq);
  return project(times, h.t, h.c.map((c, i) => lvl(h.o[i]!, c, h.h[i]!, h.l[i]!)));
}

/** Pivot status per timeframe: last close vs the floor pivot (+1 / -1). */
function pivotStatusSeries(d: OHLCV, interval: PivotInterval): (number | null)[] {
  const { times, highs, lows, closes } = d;
  if (!times || !highs || !lows) return new Array(closes.length).fill(null);
  const piv = personsPivots(times, highs, lows, closes, interval, "prior").p;
  return closes.map((c, i) => {
    const p = piv[i];
    return p == null ? null : c >= p ? 1 : -1;
  });
}

export const OC_RULE: SharedIndicatorDef = {
  id: "mpp-jp-oc-rule", label: "Afiat - Open/Close Bias", group: PERSON, kind: "state", format: "state",
  stateLabels: BULL_BEAR, panelKey: "oc_rule_d", params: [TF_DWM],
  computeSeries: (d: OHLCV, p: IndicatorParams) => ocRuleSeries(d, tfFreq(p[TF_PARAM])),
};
export const OC_SR: SharedIndicatorDef = {
  id: "mpp-jp-oc-sr", label: "Afiat - Open/Close Level", group: PERSON, format: "ratio",
  panelKey: "oc_sr_d", params: [TF_DWM],
  computeSeries: (d: OHLCV, p: IndicatorParams) => ocSrSeries(d, tfFreq(p[TF_PARAM])),
};
export const PIVOT_STATUS: SharedIndicatorDef = {
  id: "mpp-jp-pivot-status", label: "Afiat - Pivot Status", group: PERSON, kind: "state", format: "state",
  stateLabels: BULL_BEAR, panelKey: "pstatus_d", params: [TF_DWM],
  computeSeries: (d: OHLCV, p: IndicatorParams) => pivotStatusSeries(d, tfInterval(p[TF_PARAM])),
};
export const TECH_RANK: SharedIndicatorDef = {
  id: "mpp-jp-tech-rank", label: "Afiat - Composite Rank", group: familyGroup("techrank"), format: "signed",
  panelKey: "tech_rank", params: [],
  // Composite of 11 Person fields (Trend D/W/M + OC Rule W/M + Pivot Status W/M +
  // LCC + PMC MA + Vol Hist W/M, range -11..+11) — instant from the baked column;
  // no per-bar recompute (no params → always reads the panelKey).
  computeSeries: (d: OHLCV) => new Array(d.closes.length).fill(null),
};

export const PERSON_SCREENER_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  OC_RULE, OC_SR, PIVOT_STATUS, TECH_RANK,
];
